/**
 * Supabase Active Position Repository
 * Handles CRUD for active positions via Supabase
 */

import type {
  IActivePositionRepository,
  ActivePosition,
  CreatePositionData,
  UpdatePositionData,
  PositionSnapshot,
} from '../interfaces/IActivePositionRepository'
import { requireSupabaseClient } from '@/lib/supabase'

interface ActivePositionRow {
  id: string
  ticker: string
  buy_in_price: number
  opened_at: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

interface SnapshotRow {
  id: string
  position_id: string
  ticker: string
  snapshot_date: string
  close_price: number
  buy_in_price: number
  pnl: number
  pnl_percent: number
  created_at: string
}

export class SupabaseActivePositionRepository
  implements IActivePositionRepository
{
  async getPositions(): Promise<ActivePosition[]> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('active_positions')
      .select('*')
      .order('created_at', { ascending: true })
      .returns<ActivePositionRow[]>()

    if (error) {
      throw new Error(`Failed to fetch active positions: ${error.message}`)
    }

    return (data || []).map((row) => this.mapToPosition(row))
  }

  async createPosition(input: CreatePositionData): Promise<ActivePosition> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('active_positions')
      .insert({
        ticker: input.ticker.toUpperCase().trim(),
        buy_in_price: input.buyInPrice,
        opened_at: input.openedAt || null,
        notes: input.notes || null,
      } as Record<string, unknown>)
      .select('*')
      .single<ActivePositionRow>()

    if (error) {
      throw new Error(`Failed to create position: ${error.message}`)
    }

    return this.mapToPosition(data)
  }

  async updatePosition(
    id: string,
    input: UpdatePositionData
  ): Promise<ActivePosition> {
    const supabase = requireSupabaseClient()

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }

    if (input.ticker !== undefined) {
      updateData.ticker = input.ticker.toUpperCase().trim()
    }
    if (input.buyInPrice !== undefined) {
      updateData.buy_in_price = input.buyInPrice
    }
    if (input.openedAt !== undefined) {
      updateData.opened_at = input.openedAt || null
    }
    if (input.notes !== undefined) {
      updateData.notes = input.notes || null
    }

    const { data, error } = await supabase
      .from('active_positions')
      .update(updateData)
      .eq('id', id)
      .select('*')
      .single<ActivePositionRow>()

    if (error) {
      throw new Error(`Failed to update position: ${error.message}`)
    }

    return this.mapToPosition(data)
  }

  async deletePosition(id: string): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error } = await supabase
      .from('active_positions')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete position: ${error.message}`)
    }
  }

  async getSnapshots(days: number = 30): Promise<PositionSnapshot[]> {
    const supabase = requireSupabaseClient()

    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)
    const cutoff = cutoffDate.toISOString().slice(0, 10)

    const { data, error } = await supabase
      .from('position_snapshots')
      .select('*')
      .gte('snapshot_date', cutoff)
      .order('snapshot_date', { ascending: true })
      .returns<SnapshotRow[]>()

    if (error) {
      throw new Error(`Failed to fetch position snapshots: ${error.message}`)
    }

    return (data || []).map((row) => this.mapToSnapshot(row))
  }

  private mapToPosition(row: ActivePositionRow): ActivePosition {
    return {
      id: row.id,
      ticker: row.ticker,
      buyInPrice: row.buy_in_price,
      openedAt: row.opened_at || undefined,
      notes: row.notes || undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }
  }

  private mapToSnapshot(row: SnapshotRow): PositionSnapshot {
    return {
      id: row.id,
      positionId: row.position_id,
      ticker: row.ticker,
      snapshotDate: row.snapshot_date,
      closePrice: row.close_price,
      buyInPrice: row.buy_in_price,
      pnl: row.pnl,
      pnlPercent: row.pnl_percent,
    }
  }
}
